import React, { useState, useEffect, useCallback } from "react";
import { useHotel } from "../contexts/HotelContext";
import Layout from "../components/Layout";
import {
  ApiKey,
  CreateApiKeyPayload,
  getApiKeys,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  rotateApiKey,
  getAvailablePermissions,
  getPermissionDisplayName,
  groupPermissionsByCategory,
  getCategoryDisplayName,
} from "../services/apiKeysApi";

const ApiKeys: React.FC = () => {
  const { currentTenant } = useHotel();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [keyCopied, setKeyCopied] = useState(false);
  const [formData, setFormData] = useState<CreateApiKeyPayload>({
    name: "",
    permissions: [],
  });
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current tenant ID from context
  const tenantId = currentTenant?.id;

  const fetchApiKeys = useCallback(async () => {
    if (!tenantId) return;

    try {
      const data = await getApiKeys(tenantId);
      setApiKeys(data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
      setMessage("Blad ladowania kluczy API");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchAvailablePermissions = useCallback(async () => {
    if (!tenantId) return;

    try {
      const { permissions } = await getAvailablePermissions(tenantId);
      setAvailablePermissions(permissions);
    } catch (error) {
      console.error("Error fetching available permissions:", error);
      // Don't show error - user might not have permission to see this
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchApiKeys();
      fetchAvailablePermissions();
    } else {
      setLoading(false);
    }
  }, [tenantId, fetchApiKeys, fetchAvailablePermissions]);

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    if (formData.permissions.length === 0) {
      setMessage("Wybierz przynajmniej jedno uprawnienie");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const newKey = await createApiKey(tenantId, formData);
      setNewKeyValue(newKey.key || "");
      setShowCreateModal(false);
      setShowKeyModal(true);
      setFormData({ name: "", permissions: [] });
      fetchApiKeys();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message ||
          error.message ||
          "Blad tworzenia klucza API"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingKey || !tenantId) return;

    setIsSubmitting(true);
    setMessage("");

    try {
      await updateApiKey(tenantId, editingKey.id, {
        name: formData.name,
        permissions: formData.permissions,
      });
      setMessage("Klucz API zaktualizowany pomyslnie!");
      setShowEditModal(false);
      setEditingKey(null);
      setFormData({ name: "", permissions: [] });
      fetchApiKeys();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message ||
          error.message ||
          "Blad aktualizacji klucza API"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKey = async (apiKeyId: string) => {
    if (!window.confirm("Czy na pewno chcesz dezaktywowac ten klucz API?")) {
      return;
    }

    if (!tenantId) return;

    try {
      await deleteApiKey(tenantId, apiKeyId);
      setMessage("Klucz API dezaktywowany pomyslnie!");
      fetchApiKeys();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message ||
          error.message ||
          "Blad dezaktywacji klucza API"
      );
    }
  };

  const handleRotateKey = async (apiKeyId: string) => {
    if (
      !window.confirm(
        "Czy na pewno chcesz wygenerowac nowy klucz? Stary klucz przestanie dzialac."
      )
    ) {
      return;
    }

    if (!tenantId) return;

    try {
      const result = await rotateApiKey(tenantId, apiKeyId);
      setNewKeyValue(result.newKey);
      setShowKeyModal(true);
      fetchApiKeys();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message ||
          error.message ||
          "Blad rotacji klucza API"
      );
    }
  };

  const handlePermissionToggle = (permission: string) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter((p) => p !== permission)
      : [...formData.permissions, permission];
    setFormData({ ...formData, permissions: newPermissions });
  };

  const openEditModal = (apiKey: ApiKey) => {
    setEditingKey(apiKey);
    setFormData({
      name: apiKey.name,
      permissions: apiKey.permissions,
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowKeyModal(false);
    setEditingKey(null);
    setNewKeyValue("");
    setKeyCopied(false);
    setFormData({ name: "", permissions: [] });
    setMessage("");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(newKeyValue);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("pl-PL");
  };

  const groupedPermissions = groupPermissionsByCategory(availablePermissions);

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Ladowanie...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tenantId) {
    return (
      <Layout showBackButton={true}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="text-center">
              <svg
                className="h-12 w-12 text-neutral-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                Brak dostepu do organizacji
              </h2>
              <p className="text-neutral-600">
                Nie masz dostepu do zadnej organizacji.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    Klucze API
                  </h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    Zarzadzaj kluczami API dla organizacji{" "}
                    {currentTenant?.name || ""}
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <span className="text-neutral-500">
                      Tenant ID:{" "}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded font-mono">
                        {tenantId}
                      </code>
                    </span>
                    <a
                      href="/docs/api"
                      className="text-primary-600 hover:text-primary-700 hover:underline flex items-center gap-1"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                      Dokumentacja API
                    </a>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  disabled={availablePermissions.length === 0}
                  className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Utworz klucz API
                </button>
              </div>

              {availablePermissions.length === 0 && (
                <div className="mb-4 p-3 rounded-lg text-sm font-medium bg-warning-50 text-warning-700 border border-warning-200">
                  Nie masz uprawnien do tworzenia kluczy API. Skontaktuj sie z
                  administratorem.
                </div>
              )}

              {message && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                    message.includes("pomyslnie")
                      ? "bg-success-50 text-success-700 border border-success-200"
                      : "bg-error-50 text-error-700 border border-error-200"
                  }`}
                >
                  {message}
                </div>
              )}

              {apiKeys.length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-neutral-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                    Brak kluczy API
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    Utworz pierwszy klucz API, aby integrowac sie z zewnetrznymi
                    systemami.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Nazwa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Uprawnienia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Ostatnie uzycie
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Akcje
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {apiKeys.map((apiKey) => (
                        <tr key={apiKey.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                                <svg
                                  className="h-4 w-4 text-primary-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                  />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-neutral-900">
                                  {apiKey.name}
                                </div>
                                <div className="text-xs text-neutral-500">
                                  Utworzono: {formatDate(apiKey.createdAt)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {apiKey.permissions.slice(0, 3).map((perm) => (
                                <span
                                  key={perm}
                                  className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800"
                                  title={getPermissionDisplayName(perm)}
                                >
                                  {perm}
                                </span>
                              ))}
                              {apiKey.permissions.length > 3 && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-800">
                                  +{apiKey.permissions.length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                            {formatDate(apiKey.lastUsedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                apiKey.isActive
                                  ? "bg-success-100 text-success-800"
                                  : "bg-neutral-100 text-neutral-800"
                              }`}
                            >
                              {apiKey.isActive ? "Aktywny" : "Nieaktywny"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => openEditModal(apiKey)}
                              className="text-primary-600 hover:text-primary-900 mr-3"
                            >
                              Edytuj
                            </button>
                            <button
                              onClick={() => handleRotateKey(apiKey.id)}
                              className="text-warning-600 hover:text-warning-900 mr-3"
                            >
                              Rotuj
                            </button>
                            {apiKey.isActive && (
                              <button
                                onClick={() => handleDeleteKey(apiKey.id)}
                                className="text-error-600 hover:text-error-900"
                              >
                                Dezaktywuj
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Utworz nowy klucz API
              </h3>
            </div>
            <form onSubmit={handleCreateKey} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nazwa klucza
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="np. Integracja z systemem XYZ"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Uprawnienia
                  </label>
                  <p className="text-xs text-neutral-500 mb-3">
                    Wybierz uprawnienia, ktore chcesz przypisac do tego klucza
                    API.
                  </p>

                  {Object.entries(groupedPermissions).map(
                    ([category, perms]) => (
                      <div key={category} className="mb-4">
                        <h4 className="text-sm font-medium text-neutral-900 mb-2">
                          {getCategoryDisplayName(category)}
                        </h4>
                        <div className="space-y-2 ml-2">
                          {perms.map((perm) => (
                            <label
                              key={perm}
                              className="flex items-start cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(perm)}
                                onChange={() => handlePermissionToggle(perm)}
                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                              />
                              <span className="ml-2 text-sm text-neutral-700">
                                {getPermissionDisplayName(perm)}
                                <span className="block text-xs text-neutral-500">
                                  {perm}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  {availablePermissions.length === 0 && (
                    <p className="text-sm text-neutral-500 italic">
                      Brak dostepnych uprawnien.
                    </p>
                  )}
                </div>
              </div>

              {message && (
                <div className="mt-4 p-3 rounded-lg text-sm font-medium bg-error-50 text-error-700 border border-error-200">
                  {message}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting || formData.permissions.length === 0
                  }
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Tworzenie..." : "Utworz klucz"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit API Key Modal */}
      {showEditModal && editingKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Edytuj klucz API
              </h3>
            </div>
            <form onSubmit={handleUpdateKey} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nazwa klucza
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Uprawnienia
                  </label>

                  {Object.entries(groupedPermissions).map(
                    ([category, perms]) => (
                      <div key={category} className="mb-4">
                        <h4 className="text-sm font-medium text-neutral-900 mb-2">
                          {getCategoryDisplayName(category)}
                        </h4>
                        <div className="space-y-2 ml-2">
                          {perms.map((perm) => (
                            <label
                              key={perm}
                              className="flex items-start cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(perm)}
                                onChange={() => handlePermissionToggle(perm)}
                                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                              />
                              <span className="ml-2 text-sm text-neutral-700">
                                {getPermissionDisplayName(perm)}
                                <span className="block text-xs text-neutral-500">
                                  {perm}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {message && (
                <div className="mt-4 p-3 rounded-lg text-sm font-medium bg-error-50 text-error-700 border border-error-200">
                  {message}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting || formData.permissions.length === 0
                  }
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Zapisywanie..." : "Zapisz zmiany"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Key Modal (one-time display) */}
      {showKeyModal && newKeyValue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Twoj nowy klucz API
              </h3>
            </div>
            <div className="p-6">
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-warning-400 mr-2 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-warning-800">
                      Uwaga!
                    </h4>
                    <p className="text-sm text-warning-700 mt-1">
                      To jest jedyny moment, kiedy zobaczysz ten klucz. Skopiuj
                      go teraz i przechowaj w bezpiecznym miejscu.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={newKeyValue}
                  className="w-full px-3 py-2 pr-24 border border-neutral-300 rounded-lg bg-neutral-50 font-mono text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                >
                  {keyCopied ? "Skopiowano!" : "Kopiuj"}
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={closeModals}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  Rozumiem, skopiowano klucz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ApiKeys;
